import React from 'react';
import { Solicitud } from '@/types/solicitud';
import { User } from '@/types/user';
import { Empresa } from '@/types/empresa';
import { Switch } from '@/components/ui/Switch';
import { usePlazosDisponibles } from '@/hooks/usePlazosDisponibles';
import { MontoInput } from '@/components/ui/MontoInput';
import { useAuth } from '@/hooks/useAuth';

const PRODUCTO_OPTIONS = ['Nómina', 'Adelanto de Nómina', 'Enganche Hipotecario'];
const ANTIGUEDAD_OPTIONS = ['0 - 6 meses', '6 meses - 1 año', '1 año - 2 años', 'Más de 2 años'];
const POLIZA_OPTIONS = ['Ninguna', '25000', '50000', '75000', '100000'];
const SE_ENTERO_OPTIONS = ['Correo del Asesor', 'Correo Masivo', 'Flyer', 'Llamada del Asesor', 'Presentacion del Asesor', 'Recomendacion'];
const RECHAZO_RIESGOS_OPTIONS = [
  'Clave 100', 'Clave 101', 'Falta Solicitud', 'Falta Identificación',
  'Falta Comprobante de Domicilio', 'Es PEP', 'Es Persona Bloqueada',
  'Falta Aval', 'Buro del Aval', 'Falta de Liquidez', 'Mínimo Solicitado',
  'Nivel de Riesgo', 'Firma Apocrifa', 'Firma Digital', 'Contrato No Pasa',
  'Monto excede políticas', 'Por Políticas',
];

interface AutorizacionRiesgosSectionProps {
  localSolicitud: Solicitud;
  setLocalSolicitud: React.Dispatch<React.SetStateAction<Solicitud | null>>;
  setHasChanges: (v: boolean) => void;
  asesores: User[];
  allUsers?: User[]; // Todos los usuarios (para Asesor Origen)
  empresas: Empresa[];
  loadingEmpresas?: boolean;
  onAsesorFinalChange?: (newAsesorId: string) => void;
  onAsesorOrigenChange?: (newAsesorId: string) => void;
  toggleDateField: (field: keyof Solicitud, enabled: boolean) => Promise<void>;
  updatingField: string | null;
}

export function AutorizacionRiesgosSection({
  localSolicitud,
  setLocalSolicitud,
  setHasChanges,
  asesores,
  allUsers,
  empresas,
  loadingEmpresas,
  onAsesorFinalChange,
  onAsesorOrigenChange,
  toggleDateField,
}: AutorizacionRiesgosSectionProps) {
  const labelCls = "px-3 py-2 text-right font-medium text-gray-600 border border-gray-200 w-1/3";
  const inputCls = "w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  const readOnlyCls = "w-full px-2 py-1 border border-gray-300 rounded bg-gray-50";

  const { isFieldAllowed } = useAuth();
  const { plazos: plazosOptions } = usePlazosDisponibles();
  const canEditInheritedID = isFieldAllowed('inheritedID');
  const canEditOwnerID = isFieldAllowed('ownerID');
  const canSeeRegion = isFieldAllowed('regionNombre');
  const canEditClienteEmpresa = isFieldAllowed('clienteEmpresa');
  const canSeeVoboRequerido = isFieldAllowed('voboRequerido');
  const canEditClientePagadora = isFieldAllowed('clientePagadora');
  const canEditClienteNombre = isFieldAllowed('clienteNombre');
  const canEditClienteApellidoPaterno = isFieldAllowed('clienteApellidoPaterno');
  const canEditClienteApellidoMaterno = isFieldAllowed('clienteApellidoMaterno');
  const canEditClienteNuevoRenovacion = isFieldAllowed('clienteNuevoRenovacion');
  const canEditClientePuesto = isFieldAllowed('clientePuesto');
  const canEditClienteAntiguedad = isFieldAllowed('clienteAntiguedad');
  const canEditClienteClabe = isFieldAllowed('clienteClabe');
  const canEditClienteNombreJefe = isFieldAllowed('clienteNombreJefe');
  const canEditClienteCorreoJefe = isFieldAllowed('clienteCorreoJefe');
  const canEditClienteCorreo = isFieldAllowed('clienteCorreo');
  const canEditClienteSeEnteroPor = isFieldAllowed('clienteSeEnteroPor');
  const canEditProducto = isFieldAllowed('producto');
  const canEditPolizaSeguro = isFieldAllowed('polizaSeguro');
  const canEditMontoSolicitado = isFieldAllowed('montoSolicitado');
  const canEditPlazoSolicitado = isFieldAllowed('plazoSolicitado');
  const canEditSolicitudNumeroControl = isFieldAllowed('solicitudNumeroControl');
  const canSeeFechaVencimientoLineaDeCredito = isFieldAllowed('fechaVencimientoLineaDeCredito');
  const canEditMontoAutorizado = isFieldAllowed('montoAutorizado');
  const canEditMontoComision = isFieldAllowed('montoComision');
  const canEditPlazoAutorizado = isFieldAllowed('plazoAutorizado');
  const canSeeAutorizacionDG = isFieldAllowed('autorizacionDG');
  const canEditUsuarioAutorizacionRiesgos = isFieldAllowed('usuarioAutorizacionRiesgos');
  const canEditMontoTransferencia = isFieldAllowed('montoTransferencia');
  const canSeeDocumentos = isFieldAllowed('documentos');
  const canAddArchivos = isFieldAllowed('addArchivos');
  const canListaArchivos = isFieldAllowed('listaArchivos');
  const canEditFechaContacto = isFieldAllowed('fechaContacto');
  const canEditFechaEntregaARiesgos = isFieldAllowed('fechaEntregaARiesgos');
  const canEditFechaRevisionDocumentalConluida = isFieldAllowed('fechaRevisionDocumentalConluida');
  const canEditFechaAnalisisConluido = isFieldAllowed('fechaAnalisisConluido');
  const canEditFechaVoBo = isFieldAllowed('fechaVoBo');
  const canEditFechaPropuesta = isFieldAllowed('fechaPropuesta');
  const canEditFechaRechazoRiesgos = isFieldAllowed('fechaRechazoRiesgos');
  const canEditSolicitudCausaRechazoRiesgos = isFieldAllowed('solicitudCausaRechazoRiesgos');
  const canEditFechaAutorizacionDG = isFieldAllowed('fechaAutorizacionDG');
  const canEditFechaRechazoDG = isFieldAllowed('fechaRechazoDG');
  const canEditFechaCancelacionCliente = isFieldAllowed('fechaCancelacionCliente');
  const canEditFechaCancelacionCartera = isFieldAllowed('fechaCancelacionCartera');
  const canSeeSolicitudEstatus = isFieldAllowed('solicitudEstatus');
  const canSeeComentarios = isFieldAllowed('comentarios');

  const update = (field: string, value: any) => {
    setLocalSolicitud({ ...localSolicitud, [field]: value } as Solicitud);
    setHasChanges(true);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <tbody>
          {/* Asesor Final */}
          <tr>
            <td className={labelCls}>Asesor Final</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              {canEditInheritedID ? (
                <select className={inputCls} value={localSolicitud.inheritedID || ''} onChange={(e) => onAsesorFinalChange ? onAsesorFinalChange(e.target.value) : update('inheritedID', e.target.value)}>
                  <option value="">Seleccionar asesor...</option>
                  {asesores.map(a => (
                    <option key={a.ownerID} value={a.ownerID}>{a.name || a.userName}</option>
                  ))}
                </select>
              ) : (
                <input type="text" className={readOnlyCls} value={asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.name || asesores.find(a => a.ownerID === localSolicitud.inheritedID)?.userName || localSolicitud.inheritedID || ''} readOnly />
              )}
            </td>
          </tr>

          {/* Asesor Origen */}
          <tr>
            <td className={labelCls}>Asesor Origen</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              {canEditOwnerID ? (
                <select className={inputCls} value={localSolicitud.ownerID || ''} onChange={(e) => onAsesorOrigenChange ? onAsesorOrigenChange(e.target.value) : update('ownerID', e.target.value)}>
                  <option value="">Seleccionar asesor...</option>
                  {asesores.map(a => (
                    <option key={a.ownerID} value={a.ownerID}>{a.name || a.userName}</option>
                  ))}
                </select>
              ) : (
                <input type="text" className={readOnlyCls} value={asesores.find(a => a.ownerID === localSolicitud.ownerID)?.name || asesores.find(a => a.ownerID === localSolicitud.ownerID)?.userName || localSolicitud.ownerID || ''} readOnly />
              )}
            </td>
          </tr>

          {/* Region */}
          {canSeeRegion && (
          <tr>
            <td className={labelCls}>Region</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={readOnlyCls} value={localSolicitud.regionNombre || ''} readOnly />
            </td>
          </tr>
          )}

          {/* Producto */}
          {canEditProducto && (
          <tr>
            <td className={labelCls}>Producto</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.producto || ''} onChange={(e) => update('producto', e.target.value)}>
                <option value="">Seleccionar...</option>
                {PRODUCTO_OPTIONS.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Empresa */}
          {canEditClienteEmpresa && (
          <tr>
            <td className={labelCls}>Empresa</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              {loadingEmpresas ? (
                <div className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50 text-gray-500 flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Cargando empresas...
                </div>
              ) : empresas.length === 0 ? (
                <div className="w-full px-2 py-1 border border-yellow-300 rounded bg-yellow-50 text-yellow-700 text-sm">
                  No hay empresas asignadas a este asesor
                </div>
              ) : (
                <select className={inputCls} value={localSolicitud.clienteEmpresa || ''} onChange={(e) => {
                  const nombre = e.target.value;
                  const emp = empresas.find(em => em.nombre === nombre);
                  setLocalSolicitud({
                    ...localSolicitud,
                    clienteEmpresa: nombre,
                    regionNombre: emp?.region || '',
                    voboRequerido: emp?.voboRequerido || '',
                    clientePagadora: emp?.empresaPagadora || '',
                    clienteTipoEmpresa: emp?.tipo || '',
                    clienteNivelRiesgoEmpresa: emp?.nivel || '',
                  } as Solicitud);
                  setHasChanges(true);
                }}>
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(emp => (
                    <option key={emp.id || emp.nombre} value={emp.nombre}>{emp.nombre}</option>
                  ))}
                </select>
              )}
            </td>
          </tr>
          )}

          {/* Se Requiere VoBo */}
          {canSeeVoboRequerido && (
          <tr>
            <td className={labelCls}>Se Requiere VoBo</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded bg-purple-100 text-purple-800 font-medium" value={localSolicitud.voboRequerido || ''} readOnly />
            </td>
          </tr>
          )}

          {/* Pagadora */}
          {canEditClientePagadora && (
          <tr>
            <td className={labelCls}>Pagadora</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clientePagadora || ''} onChange={(e) => update('clientePagadora', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Nombre */}
          {canEditClienteNombre && (
          <tr>
            <td className={labelCls}>Nombre</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteNombre || ''} onChange={(e) => update('clienteNombre', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Apellido Paterno */}
          {canEditClienteApellidoPaterno && (
          <tr>
            <td className={labelCls}>Apellido Paterno</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteApellidoPaterno || ''} onChange={(e) => update('clienteApellidoPaterno', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Apellido Materno */}
          {canEditClienteApellidoMaterno && (
          <tr>
            <td className={labelCls}>Apellido Materno</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteApellidoMaterno || ''} onChange={(e) => update('clienteApellidoMaterno', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Correo del Cliente */}
          {canEditClienteCorreo && (
          <tr>
            <td className={labelCls}>Correo del Cliente</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="email" className={inputCls} value={localSolicitud.clienteCorreo || ''} onChange={(e) => update('clienteCorreo', e.target.value)} placeholder="correo@ejemplo.com" />
            </td>
          </tr>
          )}

          {/* Nuevo/Renovación */}
          {canEditClienteNuevoRenovacion && (
          <tr>
            <td className={labelCls}>Nuevo/Renovación</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.clienteNuevoRenovacion || ''} onChange={(e) => update('clienteNuevoRenovacion', e.target.value)}>
                <option value="">Seleccionar...</option>
                <option value="Nuevo">Nuevo</option>
                <option value="Renovacion">Renovacion</option>
              </select>
            </td>
          </tr>
          )}

          {/* Puesto */}
          {canEditClientePuesto && (
          <tr>
            <td className={labelCls}>Puesto</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clientePuesto || ''} onChange={(e) => update('clientePuesto', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Antigüedad */}
          {canEditClienteAntiguedad && (
          <tr>
            <td className={labelCls}>Antigüedad</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.clienteAntiguedad || ''} onChange={(e) => update('clienteAntiguedad', e.target.value)}>
                <option value="">Seleccionar...</option>
                {ANTIGUEDAD_OPTIONS.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Cuenta Clabe */}
          {canEditClienteClabe && (
          <tr>
            <td className={labelCls}>Cuenta Clabe</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" inputMode="numeric" pattern="[0-9]*" className={inputCls} value={localSolicitud.clienteClabe || ''} onChange={(e) => update('clienteClabe', e.target.value.replace(/\D/g, ''))} placeholder="Solo números" />
            </td>
          </tr>
          )}

          {/* Nombre de Jefe Inmediato */}
          {canEditClienteNombreJefe && (
          <tr>
            <td className={labelCls}>Nombre de Jefe Inmediato</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteNombreJefe || ''} onChange={(e) => update('clienteNombreJefe', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Correo de Jefe Inmediato */}
          {canEditClienteCorreoJefe && (
          <tr>
            <td className={labelCls}>Correo de Jefe Inmediato</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.clienteCorreoJefe || ''} onChange={(e) => update('clienteCorreoJefe', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Se entero por */}
          {canEditClienteSeEnteroPor && (
          <tr>
            <td className={labelCls}>Se entero por</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.clienteSeEnteroPor || ''} onChange={(e) => update('clienteSeEnteroPor', e.target.value)}>
                <option value="">Seleccionar...</option>
                {SE_ENTERO_OPTIONS.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Poliza de Seguro - No aplica para Adelanto de Nómina */}
          {canEditPolizaSeguro && localSolicitud.producto !== 'Adelanto de Nómina' && (
          <tr>
            <td className={labelCls}>Poliza de Seguro</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.polizaSeguro || ''} onChange={(e) => update('polizaSeguro', e.target.value)}>
                <option value="">Seleccionar...</option>
                {POLIZA_OPTIONS.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Monto Solicitado ($) */}
          {canEditMontoSolicitado && (
          <tr>
            <td className={labelCls}>Monto Solicitado ($)</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <MontoInput className={inputCls} value={localSolicitud.montoSolicitado} onChange={(n) => update('montoSolicitado', n)} />
            </td>
          </tr>
          )}

          {/* Monto Autorizado ($) */}
          {canEditMontoAutorizado && (
          <tr>
            <td className={labelCls}>Monto Autorizado ($)</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <MontoInput className={inputCls} value={localSolicitud.montoAutorizado} onChange={(n) => update('montoAutorizado', n)} />
            </td>
          </tr>
          )}

          {/* Monto Transferencia ($) */}
          {canEditMontoTransferencia && (
          <tr>
            <td className={labelCls}>Monto Transferencia ($)</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <MontoInput className={inputCls} value={localSolicitud.montoTransferencia} onChange={(n) => update('montoTransferencia', n)} />
            </td>
          </tr>
          )}

          {/* Monto Comision Autorizada ($) */}
          {canEditMontoComision && (
          <tr>
            <td className={labelCls}>Monto Comision Autorizada ($)</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <MontoInput className={inputCls} value={localSolicitud.montoComision} onChange={(n) => update('montoComision', n)} />
            </td>
          </tr>
          )}

          {/* Plazo Solicitado en meses */}
          {canEditPlazoSolicitado && (
          <tr>
            <td className={labelCls}>Plazo Solicitado en meses</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.plazoSolicitado || ''} onChange={(e) => update('plazoSolicitado', e.target.value)}>
                <option value="">Seleccionar...</option>
                {plazosOptions.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Plazo Autorizado en meses */}
          {canEditPlazoAutorizado && (
          <tr>
            <td className={labelCls}>Plazo Autorizado en meses</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.plazoAutorizado || ''} onChange={(e) => update('plazoAutorizado', e.target.value)}>
                <option value="">Seleccionar...</option>
                {plazosOptions.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Vencimiento de Línea de Cto - Solo para Adelanto de Nómina */}
          {canSeeFechaVencimientoLineaDeCredito && (localSolicitud.producto || '').toLowerCase().includes('adelanto') && (
          <tr>
            <td className={labelCls}>Vencimiento de Línea de Cto</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={readOnlyCls} value={String(localSolicitud.fechaVencimientoLineaDeCredito || '').split('T')[0].split(' ')[0]} readOnly />
            </td>
          </tr>
          )}

          {/* Numero de Control */}
          {canEditSolicitudNumeroControl && (
          <tr>
            <td className={labelCls}>Numero de Control</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={inputCls} value={localSolicitud.solicitudNumeroControl || ''} onChange={(e) => update('solicitudNumeroControl', e.target.value)} />
            </td>
          </tr>
          )}

          {/* Separador: Control de Fechas */}
          <tr className="bg-gray-100">
            <td colSpan={3} className="px-3 py-2 font-bold text-gray-700 border border-gray-300">Control de Fechas</td>
          </tr>

          {/* Solicitud */}
          {canEditFechaContacto && (
          <tr>
            <td className={labelCls}>Solicitud</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaContacto || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaContacto} onChange={(checked) => toggleDateField('fechaContacto' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Entrega a Riesgos */}
          {canEditFechaEntregaARiesgos && (
          <tr>
            <td className={labelCls}>Entrega a Riesgos</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaEntregaARiesgos || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaEntregaARiesgos} onChange={(checked) => toggleDateField('fechaEntregaARiesgos' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaContacto} />
            </td>
          </tr>
          )}

          {/* Revisión Documental Concluída */}
          {canEditFechaRevisionDocumentalConluida && (
          <tr>
            <td className={labelCls}>Revisión Documental Concluída</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaRevisionDocumentalConluida || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaRevisionDocumentalConluida} onChange={(checked) => toggleDateField('fechaRevisionDocumentalConluida' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaEntregaARiesgos} />
            </td>
          </tr>
          )}

          {/* Análisis Concluído */}
          {canEditFechaAnalisisConluido && (
          <tr>
            <td className={labelCls}>Análisis Concluído</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaAnalisisConluido || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaAnalisisConluido} onChange={(checked) => toggleDateField('fechaAnalisisConluido' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaRevisionDocumentalConluida} />
            </td>
          </tr>
          )}

          {/* Recibe VoBo */}
          {canEditFechaVoBo && (
          <tr>
            <td className={labelCls}>Recibe VoBo</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaVoBo || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaVoBo} onChange={(checked) => toggleDateField('fechaVoBo' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaAnalisisConluido} />
            </td>
          </tr>
          )}

          {/* Autorizado Riesgos */}
          {canEditFechaPropuesta && (
          <tr>
            <td className={labelCls}>Autorizado Riesgos</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaPropuesta || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaPropuesta} onChange={(checked) => toggleDateField('fechaPropuesta' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaVoBo} />
            </td>
          </tr>
          )}

          {/* Riesgos rechaza por */}
          {canEditSolicitudCausaRechazoRiesgos && (
          <tr>
            <td className={labelCls}>Riesgos rechaza por</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <select className={inputCls} value={localSolicitud.motivoNoCierre || ''} onChange={(e) => update('motivoNoCierre', e.target.value)}>
                <option value="">Seleccionar...</option>
                {RECHAZO_RIESGOS_OPTIONS.map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </td>
          </tr>
          )}

          {/* Rechazado Riesgos */}
          {canEditFechaRechazoRiesgos && (
          <tr>
            <td className={labelCls}>Rechazado Riesgos</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaRechazoRiesgos || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaRechazoRiesgos} onChange={(checked) => toggleDateField('fechaRechazoRiesgos' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Autorizacion de D.G. */}
          {canSeeAutorizacionDG && (
          <tr>
            <td className={labelCls}>Autorizacion de D.G.</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <div className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                {localSolicitud.autorizacionDG || 'Normal'}
              </div>
            </td>
          </tr>
          )}

          {/* Autorizado DG */}
          {canEditFechaAutorizacionDG && (
          <tr>
            <td className={labelCls}>Autorizado DG</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaAutorizacionDG || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaAutorizacionDG} onChange={(checked) => toggleDateField('fechaAutorizacionDG' as keyof Solicitud, checked)} color="blue" disabled={!localSolicitud.fechaPropuesta || localSolicitud.autorizacionDG !== 'Requerida'} />
            </td>
          </tr>
          )}

          {/* Rechazado DG */}
          {canEditFechaRechazoDG && (
          <tr>
            <td className={labelCls}>Rechazado DG</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaRechazoDG || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaRechazoDG} onChange={(checked) => toggleDateField('fechaRechazoDG' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Cancelado por Cliente */}
          {canEditFechaCancelacionCliente && (
          <tr>
            <td className={labelCls}>Cancelado por Cliente</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaCancelacionCliente || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaCancelacionCliente} onChange={(checked) => toggleDateField('fechaCancelacionCliente' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Cancelado por Cartera */}
          {canEditFechaCancelacionCartera && (
          <tr>
            <td className={labelCls}>Cancelado por Cartera</td>
            <td className="px-3 py-2 border border-gray-200">
              <input type="text" className={readOnlyCls} value={localSolicitud.fechaCancelacionCartera || ''} readOnly />
            </td>
            <td className="px-1 py-2 border border-gray-200 w-16 text-center">
              <Switch checked={!!localSolicitud.fechaCancelacionCartera} onChange={(checked) => toggleDateField('fechaCancelacionCartera' as keyof Solicitud, checked)} color="blue" />
            </td>
          </tr>
          )}

          {/* Estatus */}
          {canSeeSolicitudEstatus && (
          <tr>
            <td className={labelCls}>Estatus</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={readOnlyCls} value={localSolicitud.solicitudEstatus || ''} readOnly />
            </td>
          </tr>
          )}

          {/* Autorizado en Riesgos por - Siempre solo lectura, se asigna automáticamente */}
          <tr>
            <td className={labelCls}>Autorizado en Riesgos por</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <input type="text" className={readOnlyCls} value={localSolicitud.usuarioAutorizacionRiesgos || ''} readOnly />
            </td>
          </tr>

          {/* Link Documentos */}
          {canSeeDocumentos && (
          <tr>
            <td className={labelCls}>Link Documentos</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              {canAddArchivos ? (
                <input type="text" className={inputCls} value={localSolicitud.documentos || ''} onChange={(e) => update('documentos', e.target.value)} />
              ) : (
                <input type="text" className={readOnlyCls} value={localSolicitud.documentos || ''} readOnly />
              )}
            </td>
          </tr>
          )}

          {/* Comentarios */}
          {canSeeComentarios && (
          <tr>
            <td className={labelCls}>Comentarios</td>
            <td className="px-3 py-2 border border-gray-200" colSpan={2}>
              <textarea className={`${inputCls} min-h-[60px]`} value={localSolicitud.comentarios || ''} onChange={(e) => update('comentarios', e.target.value)} rows={3} />
            </td>
          </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
